import { useEffect, useRef } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"

import type { RoadmapSource } from "@productlane/zero/queries"

import {
	deserializeNegations,
	NEGATION_PARAM,
	serializeNegations,
} from "@/components/filters"
import { useRoadmapFilterState } from "./roadmap-filter-state"

const NULL_TOKEN = "_null"

const FILTER_PARAM_KEYS = {
	statuses: "statuses",
	teamIds: "teams",
	projectIds: "projects",
	priorities: "priorities",
	companyIds: "companies",
	sources: "source",
	labelIds: "labels",
} as const

type FilterStateKey = keyof typeof FILTER_PARAM_KEYS

const ALL_PARAM_VALUES = [
	...Object.values(FILTER_PARAM_KEYS),
	NEGATION_PARAM,
] as const
const FILTER_KEY_SET = new Set<string>(ALL_PARAM_VALUES)

function serializeValues(values: readonly (string | null)[]): string {
	return values.map((v) => (v === null ? NULL_TOKEN : v)).join(",")
}

function deserializeValues(param: string): Array<string | null> {
	if (!param) return []
	return param.split(",").map((v) => (v === NULL_TOKEN ? null : v))
}

function buildFilterParams(): URLSearchParams {
	const state = useRoadmapFilterState.getState()
	const params = new URLSearchParams()

	const entries: Array<[FilterStateKey, readonly (string | null)[]]> = [
		["statuses", state.statuses],
		["teamIds", state.teamIds],
		["projectIds", state.projectIds],
		["priorities", state.priorities],
		["companyIds", state.companyIds],
		["sources", state.sources],
		["labelIds", state.labelIds],
	]

	for (const [stateKey, values] of entries) {
		if (values.length > 0) {
			params.set(FILTER_PARAM_KEYS[stateKey], serializeValues(values))
		}
	}

	const negated = serializeNegations(state.negations, FILTER_PARAM_KEYS)
	if (negated) {
		params.set(NEGATION_PARAM, negated)
	}

	return params
}

export function useRoadmapFilterUrlSync() {
	const search = useSearch({ strict: false })
	const navigate = useNavigate()
	const isInitialised = useRef(false)

	useEffect(() => {
		if (isInitialised.current) return
		isInitialised.current = true

		const hasFilterParams = ALL_PARAM_VALUES.some(
			(key) => search[key] !== undefined,
		)

		if (hasFilterParams) {
			const statuses = deserializeValues(
				search[FILTER_PARAM_KEYS.statuses] ?? "",
			).filter((v): v is string => v !== null)
			const teams = deserializeValues(
				search[FILTER_PARAM_KEYS.teamIds] ?? "",
			).filter((v): v is string => v !== null)
			const projects = deserializeValues(
				search[FILTER_PARAM_KEYS.projectIds] ?? "",
			)
			const priorities = deserializeValues(
				search[FILTER_PARAM_KEYS.priorities] ?? "",
			).filter((v): v is string => v !== null)
			const companies = deserializeValues(
				search[FILTER_PARAM_KEYS.companyIds] ?? "",
			)
			const sources = deserializeValues(
				search[FILTER_PARAM_KEYS.sources] ?? "",
			).filter((v): v is RoadmapSource => v === "customers" || v === "internal")

			useRoadmapFilterState.setState({
				negations: deserializeNegations(
					search[NEGATION_PARAM] ?? "",
					FILTER_PARAM_KEYS,
				),
				statuses,
				teamIds: teams,
				projectIds: projects,
				priorities,
				companyIds: companies,
				sources,
				labelIds: deserializeValues(
					search[FILTER_PARAM_KEYS.labelIds] ?? "",
				).filter((v): v is string => v !== null),
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		const unsub = useRoadmapFilterState.subscribe(() => {
			if (!isInitialised.current) return

			const filterParams = buildFilterParams()

			void navigate({
				to: ".",
				search: (prev) => ({
					...Object.fromEntries(
						Object.entries(prev).filter(([key]) => !FILTER_KEY_SET.has(key)),
					),
					...Object.fromEntries(filterParams),
				}),
				replace: true,
			})
		})
		return unsub
	}, [navigate])
}
